// @ts-nocheck
import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import Router, { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import JobHistory from '@/components/job-history';
import Button from '@/components/button';
import Breadcrumbs from '@/components/breadcrumbs';
import { colors } from '@/theme';
import Fetch from '@/utils/Fetch';
import * as Enums from '@/utils/enums';
import Time from '@/utils/time';
import ToastContext from '@/utils/toast-context';
import ManageJobType from '@/components/modals/jobcard/manage-job-type';
import ManageInventory from '@/components/modals/inventory/manage-inventory';
import Helper from '@/utils/helper';
import ConfirmAction from '@/components/modals/confirm-action';
import BillingService from '@/services/billing-service';
import JobStatusService from '@/services/job/job-status-service';
import { JobCardLengths } from '@/utils/fieldLengths/fieldLengths';
import SubscriptionContext from '@/utils/subscription-context';
import Storage from '@/utils/storage';
import JobPropertiesColumn from '@/components/job/job-properties-column';
import PS from '@/services/permission/permission-service';
import constants from '@/utils/constants';
import AvailableTriggers from '@/components/trigger/available-triggers';
import CustomerContactLocationSelector from '@/components/selectors/customer/customer-contact-location-selector';
import CommentService from '@/services/comment/comment-service';
import StoreSelector from '@/components/selectors/store/store-selector';
import SCComboBox from '@/components/sc-controls/form-controls/sc-combobox';
import SCTextArea from '@/components/sc-controls/form-controls/sc-textarea';
import ManageJobInventoryComponent, { useJobInventoryState } from '@/PageComponents/JobInventory/ManageJobInventoryComponent';
import useRefState from '@/hooks/useRefState';
import { getSectionsFromTableData } from "@/PageComponents/SectionTable/SectionTable";
import { Box, Flex } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import warehouseService from '@/services/warehouse/warehouse-service';
import { createFileRoute } from '@tanstack/react-router'

function CreateJob(props: any) {

  const router = useRouter();
  const { module: moduleParam, moduleID, customerID } = router.query; // TODO(tanstack-router): verify router.query → useParams/useSearch
  const routeModule = isNaN(parseInt((moduleParam as any))) ? null : parseInt((moduleParam as any));

  const subscriptionContext = useContext(SubscriptionContext);

  const { data: jobTypesData } = useQuery(
    ['jobTypes'],
    () => Fetch.get({ url: '/JobType', params: { filterByStartStatus: true } })
  );

  const { data: customFieldsData } = useQuery(
    ['customFields', Enums.Module.JobCard],
    () => Fetch.get({ url: `/Option?module=${Enums.Module.JobCard}` })
  );

  const { data: queryModuleData } = useQuery(
    ['queryModule', moduleID],
    () => Fetch.get({ url: `/Query/${moduleID}` }),
    { enabled: routeModule === Enums.Module.Query && !!moduleID }
  );

  const { data: quoteModuleData } = useQuery(
    ['quoteModule', moduleID],
    () => Fetch.get({ url: `/Quote/${moduleID}` }),
    { enabled: routeModule === Enums.Module.Quote && !!moduleID }
  );

  const { data: invoiceModuleData } = useQuery(
    ['invoiceModule', moduleID],
    () => Fetch.get({ url: `/Invoice/${moduleID}` }),
    { enabled: routeModule === Enums.Module.Invoice && !!moduleID }
  );

  const { data: purchaseOrderModuleData } = useQuery(
    ['purchaseOrderModule', moduleID],
    () => Fetch.get({ url: `/PurchaseOrder/${moduleID}` }),
    { enabled: routeModule === Enums.Module.PurchaseOrder && !!moduleID }
  );

  const needsWarehouse = routeModule === Enums.Module.Quote || routeModule === Enums.Module.Invoice || routeModule === Enums.Module.PurchaseOrder;

  const { data: warehousesData } = useQuery(
    ['warehouses'],
    () => warehouseService.getWarehouses(1000),
    { enabled: needsWarehouse }
  );

  const propsQuery = queryModuleData ?? {};
  const propsQuote = quoteModuleData ?? {};
  const propsInvoice = invoiceModuleData ?? {};
  const propsPurchaseOrder = purchaseOrderModuleData ?? {};

  const [usedTriggers, setUsedTriggers] = useState<any[]>([]);
  const [accessStatus, setAccessStatus] = useState(Enums.AccessStatus.None);
  const [masterOfficeAdminPermission] = useState(PS.hasPermission(Enums.PermissionName.MasterOfficeAdmin));

  const getAccessStatus = async () => {
    const [subscriptionInfo] = await BillingService.getSubcriptionInfo(subscriptionContext);
    if (subscriptionInfo) {
      setHasCreditsAvailable(subscriptionInfo.SMSCreditsPurchased - (subscriptionInfo.SMSCreditsUsed ? subscriptionInfo.SMSCreditsUsed : 0) > 0);
      setAccessStatus(subscriptionInfo.AccessStatus);
    }
  };

  function prepareJob(defaultWarehouseParam: any) {
    let jobPrep = {
      JobInventory: [],
      CustomerID: customerID ?? null,
      JobCardWarehouses: []
    };

    if (propsQuery && propsQuery.Inventory) {
      jobPrep.JobInventory = [
        {
          InventoryID: propsQuery.Inventory.ID,
          InventoryDescription: propsQuery.Inventory.Description,
          InventoryCode: propsQuery.Inventory.Code,
          Inventory: propsQuery.Inventory,
          QuantityRequested: 1,
          StockItemStatus: Enums.StockItemStatus.WorkedOn,
          Billable: false,
          UnitAmount: 0,
          LineNumber: 0,
          WarrantyPeriod: propsQuery.Inventory.WarrantyPeriod,
          IsActive: true
        }
      ];
    }

    let defaultWarehouse = defaultWarehouseParam;

    if (propsQuote && Array.isArray(propsQuote.QuoteItems)) {

      const newQuoteSectionIdMapping = propsQuote.QuoteItems.reduce((p: any, c: any) => (
        c.InventorySectionID && !p.hasOwnProperty(c.InventorySectionID) ? {
          ...p,
          [c.InventorySectionID]: crypto?.randomUUID()
        } : p
      ), {})

      propsQuote.QuoteItems.filter((x: any) => x.IsActive && x.InventoryID).sort((a: any, b: any) => a.LineNumber - b.LineNumber).forEach((quoteItem: any, idx: any) => {
        let isTracked = Helper.isInventoryWarehoused(quoteItem.Inventory);
        jobPrep.JobInventory.push({
          ID: Helper.newGuid(),
          LineNumber: idx + 1,
          IsActive: true,
          UnitAmount: 0,
          InventoryID: quoteItem.InventoryID,
          InventoryCode: quoteItem.InventoryCode,
          InventoryDescription: quoteItem.InventoryDescription,
          Inventory: quoteItem.Inventory,
          Description: quoteItem.Description !== quoteItem.InventoryDescription ? quoteItem.Description : null,
          QuantityAllocated: 0,
          QuantityOutstanding: 0,
          QuantityReturned: 0,
          QuantityReturnPending: 0,
          QuantityRequested: parseFloat(quoteItem.Quantity),
          StockItemStatus: Enums.StockItemStatus.ItemUsed,
          Billable: true,
          StockItemType: quoteItem.StockItemType,
          InventorySectionID: newQuoteSectionIdMapping[quoteItem.InventorySectionID],
          InventorySectionName: quoteItem.InventorySectionName,
          HideLineItems: false,
          DisplaySubtotal: false,
          WarehouseID: isTracked ? (defaultWarehouse?.ID ?? null) : null,
          Warehouse: isTracked ? defaultWarehouse : null,
          UnitCostPrice: quoteItem.UnitCostPrice ?? quoteItem.Inventory?.CostPrice ?? 0,
          UnitPriceExclusive: quoteItem.UnitPriceExclusive ?? 0,
          LineDiscountPercentage: quoteItem.LineDiscountPercentage ?? 0
        });
      });
    }

    if (propsInvoice && Array.isArray(propsInvoice.InvoiceItems)) {
      const newQuoteSectionIdMapping = propsInvoice.InvoiceItems.reduce((p: any, c: any) => (
        c.InventorySectionID && !p.hasOwnProperty(c.InventorySectionID) ? {
          ...p,
          [c.InventorySectionID]: crypto?.randomUUID()
        } : p
      ), {})
      propsInvoice.InvoiceItems.filter((x: any) => x.IsActive && x.InventoryID).sort((a: any, b: any) => a.LineNumber - b.LineNumber).forEach((invoiceItem: any, idx: any) => {
        jobPrep.JobInventory.push({
          ID: Helper.newGuid(),
          LineNumber: idx + 1,
          IsActive: true,
          UnitAmount: 0,
          InventoryID: invoiceItem.InventoryID,
          InventoryCode: invoiceItem.InventoryCode,
          InventoryDescription: invoiceItem.InventoryDescription,
          Inventory: invoiceItem.Inventory,
          Description: invoiceItem.Description !== invoiceItem.InventoryDescription ? invoiceItem.Description : null,
          QuantityAllocated: 0,
          QuantityOutstanding: 0,
          QuantityReturned: 0,
          QuantityReturnPending: 0,
          QuantityRequested: parseFloat(invoiceItem.Quantity),
          StockItemStatus: Enums.StockItemStatus.ItemUsed,
          Billable: true,
          InventorySectionID: newQuoteSectionIdMapping[invoiceItem.InventorySectionID],
          InventorySectionName: invoiceItem.InventorySectionName,
          HideLineItems: false,
          DisplaySubtotal: false,
          WarehouseID: invoiceItem.WarehouseID,
          Warehouse: invoiceItem.Warehouse,
          UnitCostPrice: invoiceItem.Inventory?.CostPrice ?? 0,
          UnitPriceExclusive: invoiceItem.UnitPriceExclusive ?? 0,
          LineDiscountPercentage: invoiceItem.LineDiscountPercentage ?? 0
        });
      });
    }

    if (propsPurchaseOrder && Array.isArray(propsPurchaseOrder.PurchaseOrderItems)) {
      propsPurchaseOrder.PurchaseOrderItems.filter((x: any) => x.IsActive && x.InventoryID).sort((a: any, b: any) => a.LineNumber - b.LineNumber).forEach((poItem: any, idx: any) => {
        let isTracked = Helper.isInventoryWarehoused(poItem.Inventory);
        jobPrep.JobInventory.push({
          ID: Helper.newGuid(),
          LineNumber: idx + 1,
          IsActive: true,
          UnitAmount: 0,
          InventoryID: poItem.InventoryID,
          InventoryCode: poItem.InventoryCode,
          InventoryDescription: poItem.InventoryDescription,
          Inventory: poItem.Inventory,
          Description: poItem.Description !== poItem.InventoryDescription ? poItem.Description : null,
          QuantityAllocated: 0,
          QuantityOutstanding: 0,
          QuantityReturned: 0,
          QuantityReturnPending: 0,
          QuantityRequested: parseFloat(poItem.Quantity),
          StockItemStatus: Enums.StockItemStatus.ItemUsed,
          Billable: true,
          InventorySectionID: null,
          InventorySectionName: null,
          HideLineItems: false,
          DisplaySubtotal: false,
          WarehouseID: isTracked ? (defaultWarehouse?.ID ?? null) : null,
          Warehouse: isTracked ? defaultWarehouse : null,
          UnitCostPrice: (poItem.UnitPriceExclusive ?? 0) * (1 - ((poItem.LineDiscountPercentage ?? 0) / 100)),
          UnitPriceExclusive: poItem.Inventory?.ListPrice ?? 0,
          LineDiscountPercentage: 0
        });
      });
    }

    return jobPrep;
  }

  const [job, setJob, getJobValue] = useRefState(null);

  const [jobInitialized, setJobInitialized] = useState(false);

  useEffect(() => {
    if (jobInitialized || !router.isReady) return;

    const needsQuery = routeModule === Enums.Module.Query && !!moduleID && !queryModuleData;
    const needsQuote = routeModule === Enums.Module.Quote && !!moduleID && !quoteModuleData;
    const needsInvoice = routeModule === Enums.Module.Invoice && !!moduleID && !invoiceModuleData;
    const needsPO = routeModule === Enums.Module.PurchaseOrder && !!moduleID && !purchaseOrderModuleData;
    if (needsQuery || needsQuote || needsInvoice || needsPO) return;
    if (needsWarehouse && !warehousesData?.Results) return;

    let computedDefaultWarehouse = null;
    if (warehousesData?.Results) {
      const storeID = propsQuote?.StoreID ?? propsInvoice?.StoreID ?? propsPurchaseOrder?.StoreID;
      const isMultiStoreFromSub = (subscriptionContext as any)?.subscriptionInfo?.MultiStore ?? false;
      let warehouses = warehousesData.Results
        .filter((x: any) => x.WarehouseType === Enums.WarehouseType.Warehouse)
        .map((x: any) => ({ ...x, GroupDisplay: Enums.getEnumStringValue(Enums.WarehouseType, x.WarehouseType, true) }));
      warehouses = warehouses.filter((x: any) => {
        if (!!storeID || !isMultiStoreFromSub) { return !isMultiStoreFromSub || x.StoreID === storeID; }
        return false;
      });
      if (warehouses.length > 0) {
        computedDefaultWarehouse = warehouses.sort((a: any, b: any) => (b.IsDefault ? 1 : 0) - (a.IsDefault ? 1 : 0))[0];
      }
    }

    setJob((prepareJob as any)(computedDefaultWarehouse));
    setJobInitialized(true);
  }, [router.isReady, queryModuleData, quoteModuleData, invoiceModuleData, purchaseOrderModuleData, warehousesData, jobInitialized]);

  const [jobInventoryUsed, jobInventoryWorkedOn, updateJobInventory] = useJobInventoryState(job, setJob, getJobValue);

  const [jobInventoryUpdatedSwitch, setJobInventoryUpdatedSwitch] = useState(false);

  const jobInventoryUpdate = (key: any, value: any, orKeyValues: any, isDirty = true) => {
    updateJob(key, value, orKeyValues, isDirty);
    setJobInventoryUpdatedSwitch(!jobInventoryUpdatedSwitch);
  };

  const [updatedSwitchFirstLoad, setUpdatedSwitchFirstLoad] = useState(false);

  useEffect(() => {
    if (updatedSwitchFirstLoad) {
      getCustomerJobs(selectedCustomerJobs, customerJobsPage);
    }
    setUpdatedSwitchFirstLoad(true);
  }, [jobInventoryUpdatedSwitch]);

  const updateJob = (key: any, value: any, orKeyValues: any, isDirty = true) => {
    let newJob = { ...job };

    if (orKeyValues && orKeyValues.length > 0) {
      orKeyValues.map((item: any) => {
        newJob[item.key] = item.value;
      });
    }
    if (key) {
      newJob[key] = value;
    }
    setJob(newJob);
    setFormIsDirty(isDirty);
  };

  const [hasStatusFields, setHasStatusFields] = useState(false);
  const [statusFields, setStatusFields] = useState<any[]>([]);

  const getStatusFields = async () => {

    const fieldsRequest = await Fetch.get({
      url: '/JobStatus/JobStatusOption',
      params: {
        jobStatusID: jobStatusFirst.ID
      }
    });
    let fieldResult = fieldsRequest.Results.filter((x: any) => x.IsActive);
    setStatusFields(fieldResult);
    setHasStatusFields(fieldResult.length > 0);
  };

  const [jobStatusFirst, setJobStatusFirst] = useState<any>();

  const getJobStatusFirst = async () => {
    const jobStatusFirst = await Fetch.get({
      url: `/JobStatus/First${selectedJobType ? `?jobTypeID=${selectedJobType.ID}` : `?jobTypeID=${null}`}`,
    });
    setJobStatusFirst(jobStatusFirst);
    getWorkflow(jobStatusFirst.WorkflowID);
    updateJob("WorkflowID", jobStatusFirst.WorkflowID);
  };

  useEffect(() => {
    if (jobStatusFirst) {
      getStatusFields();
    }
  }, [jobStatusFirst]);

  const [jobItemSelection, setJobItemSelection] = useState<any>();
  const [jobItemOrder, setJobItemOrder] = useState<any>();
  const [jobSingleItem, setJobSingleItem] = useState<any>();

  const getWorkflow = async (workflowID: any) => {

    let url = workflowID ? `/Workflow/${workflowID}` : '/Workflow/GetDefaultWorkflow';
    const workflow = await Fetch.get({
      url: url
    });
    setJobItemSelection(workflow.JobItemSelection);
    setJobItemOrder(workflow.JobItemOrder);
    setJobSingleItem(workflow.SingleItem);
  };

  const [showJobInventory, setShowJobInventory] = useState(false);
  const [showJobUsedItems, setShowJobUsedItems] = useState(false);
  const [jobInventoryRequired, setJobInventoryRequired] = useState(false);
  const [materialsRequired, setMaterialsRequired] = useState(false);
  const [jobProperties, setJobProperties] = useState<any[]>([]);
  const [showProperties, setShowProperties] = useState(true);

  useEffect(() => {

    let jobProps = statusFields ? statusFields.map((field: any) => {
      return {
        Name: Enums.getEnumStringValue(Enums.JobStatusOptionName, field.JobStatusOptionName),
        Description: field.JobStatusOptionDescription,
        JobStatusOptionName: field.JobStatusOptionName,
        OptionConfiguration: field.OptionConfiguration
      };
    }) : [];

    setJobProperties(jobProps);

    let jobItemProp = jobProps.find((x: any) => x.JobStatusOptionName === Enums.JobStatusOptionName.JobItem);
    let queryHasInventory = propsQuery && propsQuery.Inventory;
    let show = !!jobItemProp || !!queryHasInventory;
    setShowJobInventory(() => show);

    let quoteHasInventory = propsQuote && Array.isArray(propsQuote.QuoteItems) && propsQuote.QuoteItems.filter((x: any) => x.InventoryID).length > 0;
    let invoiceHasInventory = propsInvoice && Array.isArray(propsInvoice.InvoiceItems) && propsInvoice.InvoiceItems.filter((x: any) => x.InventoryID).length > 0;
    let materialsProp = jobProps.find((x: any) => x.JobStatusOptionName === Enums.JobStatusOptionName.Materials);
    setShowJobUsedItems(() => quoteHasInventory || invoiceHasInventory || !!materialsProp);

    setJobInventoryRequired((jobItemProp as any) && jobItemProp.OptionConfiguration!! === Enums.OptionConfiguration.Required);
    setMaterialsRequired((materialsProp as any) && materialsProp.OptionConfiguration!! === Enums.OptionConfiguration.Required);

  }, [statusFields]);

  const handleJobPropertyChange = (propertyName: any, value: any, orKeyValues: any) => {
    updateJob(propertyName, value, orKeyValues, true);
  };

  const [itemID, setItemID] = useState<any>();
  const [linkModule, setLinkModule] = useState<any>();

  const moduleInitialized = useRef(false);

  useEffect(() => {
    if (moduleInitialized.current || !router.isReady || !jobInitialized) return;
    moduleInitialized.current = true;

    if (!isNaN((routeModule as any))) {
      if (routeModule == Enums.Module.Customer) {
        selectCustomer(moduleID);
      }
      if (routeModule == Enums.Module.Project) {
        setCanChangeCustomer(false);
        selectCustomer(customerID);
      }
      if (routeModule == Enums.Module.Quote) {
        setCanChangeCustomer(false);
        selectCustomer(customerID);
        setItemID(moduleID);
        setLinkModule(routeModule);
        if (!!propsQuote.Store) {
          setSelectedStore(propsQuote.Store);
        }
      }
      if (routeModule === Enums.Module.PurchaseOrder) {
        setItemID(moduleID);
        setLinkModule(routeModule);
        if (!!propsPurchaseOrder.Store) {
          setSelectedStore(propsPurchaseOrder.Store);
        }
      }
      if (routeModule == Enums.Module.Invoice) {
        setCanChangeCustomer(false);
        selectCustomer(customerID);
        setItemID(moduleID);
        setLinkModule(routeModule);
        if (!!propsInvoice?.Store) {
          setSelectedStore(propsInvoice.Store);
        }
      }
      if (routeModule == Enums.Module.Query) {
        setCanChangeCustomer(false);
        selectCustomer(customerID);
        setItemID(moduleID);
        setLinkModule(routeModule);
        if (propsQuery) {
          setJobDesc(propsQuery.Description);
        }
        if (!!propsQuery.Store) {
          setSelectedStore(propsQuery.Store);
        }
      }
    }
    getAccessStatus();
    getStore();
  }, [router.isReady, jobInitialized]);

  useEffect(() => {
    if (accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess) {
      Helper.nextRouter(Router.replace, "/");
    }
  }, [accessStatus]);

  const selectCustomer = async (id: any) => {
    if (id) {
      const customer = await Fetch.get({
        url: `/Customer/${id}`,
      });
      setSelectedCustomer(customer);
      setCustomerName(customer.CustomerName);

      const contactId = (propsQuery?.CustomerContactID || propsQuote?.CustomerContactID)
      const locationId = (propsQuery?.LocationID || propsQuote?.LocationID)

      setSelectedContact(customer.Contacts ? (contactId && customer.Contacts.find((x: any) => x.ID === contactId) || customer.Contacts[0]) : undefined);
      setSelectedLocation(customer.Locations ? (locationId && customer.Locations.find((x: any) => x.ID === locationId) || customer.Locations[0]) : undefined);
    }
  };

  const toast = useContext(ToastContext);

  // STORES

  const [isMultiStore, setIsMultiStore] = useState(false);
  const [stores, setStores] = useState<any[]>([]);
  const [storesTotalResults, setStoresTotalResults] = useState<any>();
  const [storeSearch, setStoreSearch] = useState('');
  const [selectedStore, setSelectedStore] = useState<any>();

  useEffect(() => {
    if (!job) return;
    let employees = (job as any).Employees;
    let suppliers = (job as any).Suppliers;
    let employeeChanged = false;
    let supplierChanged = false;
    if ((job as any).Employees && (job as any).Employees.length > 0) {
      employees = [];
      employeeChanged = true;
    }
    if ((job as any).Suppliers && (job as any).Suppliers.length > 0) {
      suppliers = [];
      supplierChanged = true;
    }

    let message = employeeChanged && !supplierChanged ? "Employees have been cleared" :
      !employeeChanged && supplierChanged ? "Supplier has been cleared" :
        employeeChanged && supplierChanged ? "Employees and supplier have been cleared" : null;

    if (message) {
      toast.setToast!!!({
        message: message,
        show: true,
        type: 'success'
      });
    }

    setJob({ ...job, Employees: employees, Suppliers: suppliers, StoreID: selectedStore?.ID ?? null });
  }, [selectedStore]);

  const getStore = async () => {
    const storesResult = await Fetch.get({
      url: `/Store/GetEmployeeStores?employeeID=${Storage.getCookie(Enums.Cookie.employeeID)}&searchPhrase=`,
    });
    setStores(storesResult.Results)
    if (storesResult.TotalResults.length === 1) {
      setSelectedStore(storesResult.Results.find((x: any) => x.IsActive) || null)
    }
    setIsMultiStore(storesResult.TotalResults > 1);
  };

  const [selectedCustomerJobs, setCustomerJobs] = useState<any[]>([]);
  const [customerJobsPage, setCustomerJobsPage] = useState(0);
  const [searching, setSearching] = useState(false);
  const [jobSearching, setJobSearching] = useState(false);
  const [canLoadMoreJobs, setCanLoadMoreJobs] = useState(true);

  const [selectedContact, setSelectedContact] = useState<any>();
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSMS, setSendSMS] = useState(true);
  const [hasCreditsAvailable, setHasCreditsAvailable] = useState<any>();

  const [selectedLocation, setSelectedLocation] = useState<any>();

  // JOB TYPE

  const [jobTypes, setJobTypes] = useState<any[]>([]);

  useEffect(() => {
    if (jobTypesData?.Results && jobTypes.length === 0) {
      setJobTypes(jobTypesData.Results);
    }
  }, [jobTypesData]);
  const [jobTypeSearch, setJobTypeSearch] = useState('');
  const [selectedJobTypeID, setSelectedJobTypeID] = useState<any>();
  const [selectedJobType, setSelectedJobType] = useState<any>(null);

  useEffect(() => {
    if (selectedJobType) {
      getJobStatusFirst();
    } else {
      getWorkflow();
    }
  }, [selectedJobType]);

  const handleJobTypeChange = (e: any) => {
    setJobTypeSearch(e.target.value);
    setFormIsDirty(true);
  };

  const [showManageJobType, setShowManageJobType] = useState(false);

  const addNewJobType = () => {
    setShowManageJobType(true);
  };

  const onJobTypeSave = (jobType: any) => {
    if (jobType) {
      setJobTypes([...jobTypes, jobType]);
      setSelectedJobType(jobType);
      setJobTypeSearch(jobType.Name);
      setFormIsDirty(true);
      toast.setToast!!!({
        message: 'Job type created successfully',
        show: true,
        type: 'success'
      });
    }
    setShowManageJobType(false);
  };

  const [jobDesc, setJobDesc] = useState('');
  const [selectedJob, setSelectedJob] = useState<any>();

  const [submitting, setSubmitting] = useState(false);

  // CUSTOMERS

  const [selectedCustomer, setSelectedCustomer] = useState<any>();
  const [canChangeCustomer, setCanChangeCustomer] = useState(true);
  const [selectedCustomerName, setCustomerName] = useState('');

  const [inputErrors, setInputErrors] = useState<any>({});
  const [formIsDirty, setFormIsDirty] = useState(false);
  const [confirmOptions, setConfirmOptions] = useState(Helper.initialiseConfirmOptions());
  Helper.preventRouteChange(formIsDirty, setFormIsDirty, setConfirmOptions, preSubmitJob);

  const handleJobDescChange = (e: any) => {
    setJobDesc(e.value);
    setFormIsDirty(true);
  };

  const selectJob = (job: any) => {
    if (job == selectedJob) {
      setSelectedJob(undefined);
    } else {
      setSelectedJob(job);
    }
  };

  const [customerJobSearch, setCustomerJobSearch] = useState('');

  async function getCustomerJobs(currentJobs: any, page: any) {
    setJobSearching(true);

    let inventoryItems = (job as any)?.JobInventory?.filter((x: any) => x.IsActive && Helper.isNullOrUndefined(x.ProductID)) ?? [];
    let assetItems = (job as any)?.JobInventory?.filter((x: any) => x.IsActive && !Helper.isNullOrUndefined(x.ProductID)) ?? [];

    const params = {
      CustomerID: selectedCustomer ? selectedCustomer.ID : null,
      InventoryIDList: inventoryItems ? inventoryItems.map((x: any) => x.InventoryID) : null,
      ProductIDList: assetItems ? assetItems.map((x: any) => x.ProductID) : null,
      IncludeClosed: true,
      PageSize: 5,
      PageIndex: page,
      SearchPhrase: customerJobSearch,
    };

    const allJobs = await Fetch.post({
      url: `/Job/GetJobHistory`,
      params: params,
      toastCtx: toast
    });

    let newJobs = [];
    if (page != customerJobsPage) {
      newJobs.push(...currentJobs);
    }
    newJobs.push(...allJobs.Results);
    setCustomerJobs(newJobs);
    setJobSearching(false);

    if (allJobs.ReturnedResults < 5) {
      setCanLoadMoreJobs(false);
    } else if (newJobs.length == allJobs.TotalResults) {
      setCanLoadMoreJobs(false);
    } else {
      setCanLoadMoreJobs(true);
    }
  }

  const customerJobSearchLoadRef = useRef(false);
  useEffect(() => {
    if (customerJobSearchLoadRef.current) {
      getCustomerJobs([], 0);
    }
    customerJobSearchLoadRef.current = true;
  }, [customerJobSearch]);

  function loadMoreJobs() {
    setCustomerJobsPage(customerJobsPage + 1);
    getCustomerJobs(selectedCustomerJobs, customerJobsPage + 1)
  }

  useEffect(() => {
    if (!job) return;
    let nonAssetItems = (job as any).JobInventory.filter((x: any) => Helper.isNullOrUndefined(x.ProductID));
    setJob({
      ...job,
      CustomerID: selectedCustomer ? selectedCustomer.ID : null,
      Customer: selectedCustomer || null,
      JobInventory: nonAssetItems ? nonAssetItems : (job as any).JobInventory,
    });
  }, [selectedCustomer]);

  const firstCustomerUpdate = useRef(true);

  useEffect(() => {
    setCustomerJobsPage(0);
    setCanLoadMoreJobs(true);

    if (!Helper.isNullOrUndefined(selectedCustomer)) {
      getCustomerJobs([], 0);
      setSelectedJob(undefined);
    } else {
      setCustomerJobs([]);
    }
    setCustomerName(selectedCustomer ? selectedCustomer.CustomerName : '');

    if (firstCustomerUpdate.current) firstCustomerUpdate.current = false;
    else setFormIsDirty(true);
  }, [selectedCustomer]);

  const firstContactUpdate = useRef(true);

  useEffect(() => {
    setTimeout(() => {
      setJob((_job: any) => {
        return {
          ..._job,
          CustomerContactID: selectedContact ? selectedContact.ID : null,
          Contact: selectedContact || null
        }
      });

      if (firstContactUpdate.current) firstContactUpdate.current = false;
      else setFormIsDirty(true);
    }, 1);
  }, [selectedContact]);

  const firstLocationUpdate = useRef(true);

  useEffect(() => {
    if (!job) return;
    setJob({
      ...job,
      LocationID: selectedLocation ? selectedLocation.ID : null,
      Location: selectedLocation
    });

    if (firstLocationUpdate.current) firstLocationUpdate.current = false;
    else setFormIsDirty(true);
  }, [selectedLocation]);

  // SUBMIT JOB

  const validate = () => {

    let newInputErrors = {};

    if (!selectedCustomer) {
      (newInputErrors as any).Customer = 'Cannot be empty';
    }

    if (!selectedJobType) {
      (newInputErrors as any).JobType = 'Cannot be empty';
    }

    if (!selectedContact) {
      (newInputErrors as any).Contact = 'Cannot be empty';
    }

    if (Helper.isNullOrWhitespace(jobDesc)) {
      (newInputErrors as any).jobDesc = 'Cannot be empty';
    } else if (jobDesc.length > JobCardLengths.Description) {
      (newInputErrors as any).jobDesc = `Cannot be greater than ${JobCardLengths.Description} characters`;
    }

    if (isMultiStore && !selectedStore) {
      (newInputErrors as any).selectedStore = "Store required";
    }

    if (jobInventoryRequired) {
      let activeJobInventory = job.JobInventory.filter!!!((x: any) => x.IsActive == true && x.StockItemStatus === Enums.StockItemStatus.WorkedOn);
      if (activeJobInventory.length == 0) {
        (newInputErrors as any).JobInventory = 'Customer assets are required';
      }
    }

    if (materialsRequired) {
      let activeJobInventory = job.JobInventory.filter!!!((x: any) => x.IsActive == true && x.StockItemStatus === Enums.StockItemStatus.ItemUsed);
      if (activeJobInventory.length == 0) {
        (newInputErrors as any).Materials = 'Materials are required';
      }
    }

    let prelimIsValid = Object.keys(newInputErrors).length === 0;

    const [statusValid, errors] = JobStatusService.validateJob(job, jobProperties);

    if (!statusValid) {
      prelimIsValid = false;
      Object.keys(errors).forEach((key: any) => {
        newInputErrors[key] = errors[key];
      });
    }

    setInputErrors(newInputErrors);

    return { jobValid: prelimIsValid, customError: null };
  };

  async function preSubmitJob(saveType = 'create') {

    let submitFinished = false;
    let { jobValid, customError } = validate();

    if (jobValid) {
      submitFinished = await submitJob(saveType);
    } else {
      toast.setToast!!!({
        message: customError ? customError : 'There are errors on the page',
        show: true,
        type: Enums.ToastType.error
      });
    }

    return submitFinished;
  }

  async function submitJob(saveType = 'create') {
    let submitFinished = false;
    setSubmitting(true);

    let jobContactId = selectedContact ? selectedContact.ID : '';
    let jobLocationId = selectedLocation ? selectedLocation.ID : null;

    let contactSendList = [];
    let emailSendList = [];
    let smsSendList = [];

    if (sendEmail || sendSMS) {
      contactSendList.push(((jobContactId as any) as any));
      if (sendEmail) {
        emailSendList.push(((jobContactId as any) as any));
      }
      if (sendSMS) {
        smsSendList.push(((jobContactId as any) as any));
      }
    }

    let jobTemp = job;
    let now = Time.now();

    const sections = getSectionsFromTableData(jobTemp.JobInventory!!!, 'InventorySectionName', 'InventorySectionID', Enums.Module.JobCard, (null as any))

    jobTemp = {
      ...jobTemp,
      CustomerID: selectedCustomer.ID,
      CustomerContactID: jobContactId,
      LocationID: jobLocationId,
      Description: jobDesc,
      StartDate: (Time.toISOString(now)),
      StoreID: selectedStore ? selectedStore.ID : stores?.find((x: any) => x.IsActive)?.ID || null,
      JobTypeID: selectedJobType?.ID,
      JobTypeName: selectedJobType?.Name,
    };

    if (jobTemp.JobInventory!!!) {
      let activeJobInventory = jobTemp.JobInventory.filter!!!((x: any) => x.IsActive);
      jobTemp["JobInventory"] = activeJobInventory;
    }

    if (selectedJob) {
      jobTemp['PreviousJobNumber'] = selectedJob;
    }

    if (routeModule) {
      if (routeModule == Enums.Module.Project) {
        jobTemp['ProjectID'] = moduleID;
      }
    }

    let params = {
      Job: jobTemp,
      InventorySections: sections,
      ContactSendList: contactSendList,
      EmailSendList: emailSendList,
      SMSSendList: smsSendList,
      UsedTriggers: usedTriggers
    };

    if (itemID && linkModule) {
      params = {
        ...params,
        ItemID: itemID,
        Module: linkModule
      };
    }

    const jobPost = await Fetch.post({
      url: `/Job`,
      params: params,
      toastCtx: toast
    });

    if (jobPost.ID) {

      Helper.mixpanelTrack(constants.mixPanelEvents.createJob, {
        "jobID": jobPost.ID
      });

      if (jobTemp.Employees!!! && jobTemp.Employees.length!!! > 0) {
        Helper.mixpanelTrack(constants.mixPanelEvents.jobEmployeeAssigned, {
          "jobID": jobPost.ID
        });
      }

      submitFinished = true;
      setFormIsDirty(false);
      await Helper.waitABit();

      toast.setToast!!!({
        message: 'Job created successfully',
        show: true,
        type: 'success'
      });

      // add comment to calling module
      if (routeModule && moduleID) {
        await CommentService.createComment(`Job Created - ${jobPost.JobCardNumber}`, routeModule, moduleID, jobPost.StoreID, false);
      }

      if (saveType == 'create') {
        if (routeModule == Enums.Module.Project) {
          Helper.nextRouter(Router.push, `/project/${moduleID}?tab=jobs`);
        } else {
          Helper.nextRouter(Router.push, '/job/[id]', `/job/${jobPost.ID}?tab=jobs`);
        }
      } else if (saveType == 'duplicate') {
        setJobDesc('');
        updateJob('JobInventory', [], null, true);
        setSubmitting(false);
      } else if (saveType == 'add') {
        Router.reload();
      }
    } else {
      setSubmitting(false);
    }

    return submitFinished;
  }

  const usedTriggersChanged = (_usedTriggers: any) => {
    setUsedTriggers(_usedTriggers);
  }

  const mobileView = useMediaQuery('(max-width: 700px)')

  if (!jobInitialized || job === null) return null;

  return (
    <div className="manage-container">
      <div className="row">
        <div className="title">
          <Breadcrumbs currPage={{ text: 'Create Job', link: '/job/create', type: 'create' }} />
        </div>
      </div>
      <Flex wrap={'wrap'}>
        <div style={{ flexGrow: 1 }}>
          {isMultiStore ?
            <div className="create-job-section">

              <StoreSelector
                error={inputErrors.selectedStore}
                required={true}
                selectedStore={selectedStore}
                setSelectedStore={setSelectedStore}
              />

            </div> : ''
          }

          <CustomerContactLocationSelector isNew={true}
            selectedCustomer={selectedCustomer} setSelectedCustomer={setSelectedCustomer} canChangeCustomer={canChangeCustomer}
            selectedContact={selectedContact} setSelectedContact={setSelectedContact}
            selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation}
            hasSMSCreditsAvailable={hasCreditsAvailable} sendEmail={sendEmail} setSendEmail={setSendEmail} sendSMS={sendSMS} setSendSMS={setSendSMS}
            inputErrors={inputErrors} accessStatus={accessStatus} sendFromContact={true} cypressCustomer={"data-cy-customer"}
            cypressContact={"data-cy-contact"} cypressLocation={"data-cy-location"} iconMode />

          <div className="create-job-section">

            <SCComboBox
              addOption={(masterOfficeAdminPermission ? { text: 'Add new job type', action: () => addNewJobType() } : "")}
              label="Job Type"
              options={jobTypes}
              dataItemKey={"ID"}
              textField={"Name"}
              onChange={setSelectedJobType}
              placeholder={"Select job type"}
              value={selectedJobType}
              error={inputErrors.JobType}
              required={true}
              canClear={false}
              canSearch={false}
            />

            {showManageJobType ?
              <ManageJobType isNew={true} onJobTypeSave={onJobTypeSave} accessStatus={accessStatus} cypress={"data-cy-jobtype"} /> : ''
            }

            <SCTextArea
              label="Description of the job"
              required={true}
              error={inputErrors.jobDesc}
              value={jobDesc}
              onChange={handleJobDescChange}
              showCountMax={JobCardLengths.Description}
            />

          </div>

          {showJobInventory &&
            <>

              <ManageJobInventoryComponent
                accessStatus={accessStatus}
                allowNonEmployee={false}
                inputErrors={inputErrors}
                customerZone={false}
                fromCreateJob={true}
                fromStatusChange={false}
                job={job}
                jobInventoryUsed={jobInventoryUsed}
                jobInventoryWorkedOn={jobInventoryWorkedOn}
                stockItemStatus={Enums.StockItemStatus.WorkedOn}
                key={11}
                onUpdate={(rowVersion: any, jobInventory: any, stockItemStatus: any) => {
                  updateJobInventory(rowVersion, jobInventory, stockItemStatus);
                  setJobInventoryUpdatedSwitch((jius: any) => !jius);
                }}
              />

            </>
          }

          {(showJobUsedItems || jobInventoryUsed.length > 0) && <>
            <ManageJobInventoryComponent
              accessStatus={accessStatus}
              allowNonEmployee={false}
              inputErrors={{ JobInventory: inputErrors?.Materials }}
              customerZone={false}
              fromCreateJob={true}
              fromStatusChange={false}
              job={job}
              jobInventoryUsed={jobInventoryUsed}
              jobInventoryWorkedOn={jobInventoryWorkedOn}
              stockItemStatus={Enums.StockItemStatus.ItemUsed}
              key={12}
              onUpdate={(rowVersion: any, jobInventory: any, stockItemStatus: any) => {
                updateJobInventory(rowVersion, jobInventory, stockItemStatus);
                setJobInventoryUpdatedSwitch((jius: any) => !jius);
              }}
            />
          </>}

          {jobProperties.length > 0 ?
            showProperties
              ? <div className="more" onClick={() => setShowProperties(false)}>
                Show less
              </div>
              : <div className="more" onClick={() => setShowProperties(true)}>
                Show more
              </div> : ''
          }
          <div style={{ display: (showProperties ? "initial" : "none"), maxWidth: constants.maxFormWidth }}>
            <JobPropertiesColumn job={job} jobProperties={jobProperties} updateJobProperty={handleJobPropertyChange} inputErrors={inputErrors}
              showAll={true} key={0} customFields={customFieldsData?.Results ?? []} groupSize={2} selectedStore={selectedStore} accessStatus={accessStatus} />
          </div>

          <br />

          {jobStatusFirst ?
            <AvailableTriggers module={Enums.Module.JobCard} moduleStatus={jobStatusFirst} moduleType={selectedJobType?.ID} triggerListChanged={usedTriggersChanged} /> : ''
          }

          <div className="create-buttons">
            <Button extraClasses="right-margin fit-content" text="Create Job" onClick={() => preSubmitJob('create')} disabled={submitting} />
          </div>

        </div>

        {
          !mobileView &&
          <Box mr={{ base: 0, 'lg': 50 }}>
            <JobHistory
              canLoadMoreJobs={canLoadMoreJobs}
              customer={selectedCustomerName}
              jobs={selectedCustomerJobs}
              jobSearching={jobSearching}
              loadMoreJobs={loadMoreJobs}
              search={customerJobSearch}
              setSearch={setCustomerJobSearch}
              selectedJob={selectedJob}
              setSelectedJob={selectJob}
            />
          </Box>
        }

      </Flex>

      <ConfirmAction options={confirmOptions} setOptions={setConfirmOptions} />

      <style jsx>{`
        .column {
          display: flex;
          flex-direction: column;
          width: 100%;
        }
        .column-margin {
          margin-left: 24px;
        }
        .button-container {
          flex-shrink: 0;
          width: 10rem;
        }
        .button-container :global(.button){
          margin-top: 0.5rem;
        }
        .row {
          display: flex;
          justify-content: space-between;
        }

        .more {
          color: ${colors.bluePrimary};
          cursor: pointer;
          display: flex;
          justify-content: flex-start;
          margin-top: 1rem;
        }
        .edit {
          cursor: pointer;
        }        
        .cancel-link {
          color: ${colors.bluePrimary};
          cursor: pointer;
          font-size: 0.875rem;
        }
        .create-buttons {
          display: flex;
          justify-content: space-between;
        }

        .create-job-section {
        }
      `}</style>
    </div>
  );
}

export default CreateJob;

export const Route = createFileRoute('/_authenticated/job/create')({ component: CreateJob })
